import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { CompanyProfile } from '../company.model';



interface CompanyStats {
  totalWorkers:    number;
  totalJobs:       number;
  completedJobs:   number;
  totalHoursLogged: number;
  totalWorkLogs:   number;
}

@Component({
  selector:    'app-company-profile',
  templateUrl: './company-profile.component.html',
  styleUrls:   ['./company-profile.component.css']
})
export class CompanyProfileComponent implements OnInit, OnDestroy {

  private readonly api = `${environment.apiUrl}/api/company`;

  // ── State ─────────────────────────────────────────────
  profile:  CompanyProfile | null = null;
  stats:    CompanyStats   | null = null;

  loadingProfile = true;
  loadingStats   = true;
  saving         = false;
  editMode       = false;

  // ── Logo upload state ────────────────────────────────
  uploadingLogo   = false;
  uploadProgress  = 0;
  logoError:   string | null = null;
  logoSuccess: string | null = null;

  // ── General message ──────────────────────────────────
  saveError:   string | null = null;
  saveSuccess: string | null = null;

  // ── Editable form model ──────────────────────────────
  editForm = {
    name:        '',
    industry:    '',
    address:     '',
    phone:       '',
    email:       '',
    website:     '',
    description: ''
  };

  // Local preview URL shown immediately after upload
  // before the page reloads — improves perceived performance
  localLogoPreview: string | null = null;

  private subs: Subscription[] = [];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadProfile();
    this.loadStats();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }


  // ─────────────────────────────────────────────────────
  // LOAD
  // ─────────────────────────────────────────────────────

  loadProfile(): void {
    this.loadingProfile = true;
    this.http.get<CompanyProfile>(`${this.api}/profile`).subscribe({
      next: (profile) => {
        this.profile        = profile;
        this.loadingProfile = false;
        this.populateEditForm(profile);
      },
      error: (err) => {
        console.error('Failed to load company profile:', err);
        this.loadingProfile = false;
      }
    });
  }

  loadStats(): void {
    this.loadingStats = true;
    this.http.get<CompanyStats>(`${this.api}/stats`).subscribe({
      next: (stats) => {
        this.stats        = stats;
        this.loadingStats = false;
      },
      error: (err) => {
        console.error('Failed to load company stats:', err);
        this.loadingStats = false;
      }
    });
  }

  private populateEditForm(profile: CompanyProfile): void {
    this.editForm = {
      name:        profile.name        ?? '',
      industry:    profile.industry    ?? '',
      address:     profile.address     ?? '',
      phone:       profile.phone       ?? '',
      email:       profile.email       ?? '',
      website:     profile.website     ?? '',
      description: profile.description ?? ''
    };
  }


  // ─────────────────────────────────────────────────────
  // LOGO UPLOAD
  // ─────────────────────────────────────────────────────

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Reset all message state
    this.logoError   = null;
    this.logoSuccess = null;

    // ── Client-side validation ──────────────────────────

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.logoError = 'Only JPEG, PNG, and WebP images are allowed.';
      return;
    }

    const maxSizeMb = 2;
    if (file.size > maxSizeMb * 1024 * 1024) {
      this.logoError = `Image is too large (${Math.round(file.size / 1024 / 1024 * 10) / 10}MB). Maximum size is ${maxSizeMb}MB.`;
      return;
    }

    // Show a local preview immediately using a FileReader —
    // this gives instant visual feedback before the upload completes.
    const reader = new FileReader();
    reader.onload = (e) => {
      this.localLogoPreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);

    // Start the upload.
    this.uploadLogo(file);
  }

  private uploadLogo(file: File): void {
    this.uploadingLogo  = true;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('file', file, file.name);

    // Use reportProgress so we can show an upload progress bar.
    const req = this.http.post<{ logoUrl: string; message: string }>(
      `${this.api}/upload-logo`,
      formData,
      { reportProgress: true, observe: 'events' }
    );

    const sub = req.subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress = Math.round(100 * event.loaded / event.total);
        }

        if (event.type === HttpEventType.Response) {
          const body = event.body;

          if (body?.logoUrl && this.profile) {
            // Update the profile object with the new logo URL.
            // This persists the change in memory until the next full reload.
            this.profile = { ...this.profile, logoUrl: body.logoUrl };

            // Clear the local preview — we now have the real URL.
            this.localLogoPreview = null;
          }

          this.uploadingLogo  = false;
          this.uploadProgress = 100;
          this.logoSuccess    = 'Logo updated successfully.';

          // Store the logo URL in sessionStorage so that other
          // components (PDF export, sidebar) can use it without
          // an extra API call.
          if (body?.logoUrl)
            sessionStorage.setItem('companyLogoUrl', body.logoUrl);

          // Clear success message after 4 seconds.
          setTimeout(() => { this.logoSuccess = null; }, 4000);
        }
      },

      error: (err) => {
        console.error('Logo upload error:', err);
        this.uploadingLogo    = false;
        this.localLogoPreview = null;

        // Show the server error message if available, otherwise a generic one.
        this.logoError = err?.error?.error
          ?? 'Logo upload failed. Please check your connection and try again.';
      }
    });

    this.subs.push(sub);
  }


  // ─────────────────────────────────────────────────────
  // PROFILE EDIT / SAVE
  // ─────────────────────────────────────────────────────

  toggleEdit(): void {
    this.editMode  = !this.editMode;
    this.saveError   = null;
    this.saveSuccess = null;

    if (this.editMode && this.profile)
      this.populateEditForm(this.profile);
  }

  saveProfile(): void {
    if (this.saving || !this.editForm.name.trim()) return;

    this.saving      = true;
    this.saveError   = null;
    this.saveSuccess = null;

    const sub = this.http.put<CompanyProfile>(
      `${this.api}/profile`,
      this.editForm
    ).subscribe({
      next: (updated) => {
        this.profile     = updated;
        this.saving      = false;
        this.editMode    = false;
        this.saveSuccess = 'Profile updated successfully.';
        setTimeout(() => { this.saveSuccess = null; }, 4000);
      },
      error: (err) => {
        console.error('Profile save error:', err);
        this.saving    = false;
        this.saveError = err?.error?.error
          ?? 'Could not save profile. Please try again.';
      }
    });

    this.subs.push(sub);
  }

  cancelEdit(): void {
    this.editMode  = false;
    this.saveError = null;
    if (this.profile) this.populateEditForm(this.profile);
  }


  // ─────────────────────────────────────────────────────
  // DISPLAY HELPERS
  // ─────────────────────────────────────────────────────

  // The logo to display — prefer local preview during upload,
  // then the server URL once upload is complete.
  get displayLogoUrl(): string | null {
    return this.localLogoPreview ?? this.profile?.logoUrl ?? null;
  }

  get companyInitials(): string {
    if (!this.profile?.name) return '?';
    return this.profile.name
      .split(' ')
      .slice(0, 2)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
  }

  get memberSince(): string {
    if (!this.profile?.createdAt) return '';
    return new Date(this.profile.createdAt).toLocaleDateString('en-GB', {
      month: 'long',
      year:  'numeric'
    });
  }
}